import { SPACE_METADATA_TEXT_FIELD_KEYS } from '../constants/spaceMetadata'
import {
    EditTownMetadataFormValues,
    FAQFormValue,
    FeatureFormValue,
    FormFallbackValues,
    MAX_FEATURES,
    MediaFormValue,
    MIN_FEATURES,
    ServerSpaceMetadata,
    SOCIAL_LINK_DEFAULTS,
    SocialLinkFormValue,
    TownSocialLink,
} from '../schema/spaceMetadata.schema'

export function parseJsonField<T>(value: string | undefined, fallback: T): T {
    if (!value) {
        return fallback
    }
    try {
        return JSON.parse(value) as T
    } catch (error) {
        return fallback
    }
}

export function getDefaultSocialLinks(): SocialLinkFormValue[] {
    return SOCIAL_LINK_DEFAULTS.map((item) => ({
        type: item.type,
        label: item.label,
        url: '',
    }))
}

export function inferMediaTypeFromUrl(url: string | undefined): MediaFormValue['type'] {
    if (!url) {
        return 'image'
    }

    let pathname: string

    // Try to parse as a valid absolute URL
    try {
        const urlObj = new URL(url)
        pathname = urlObj.pathname
    } catch {
        // Fall back to removing query/hash with regex
        // Split on '?' or '#' and take the first part
        pathname = url.split(/[?#]/)[0]
    }

    // Check the extension on the pathname
    const lower = pathname.toLowerCase()
    if (
        lower.endsWith('.mp4') ||
        lower.endsWith('.mov') ||
        lower.endsWith('.webm') ||
        lower.endsWith('.avi')
    ) {
        return 'video'
    }
    return 'image'
}

export function getMediaTypeFromFile(file: File): NonNullable<MediaFormValue['type']> {
    if (file.type.startsWith('video/')) {
        return 'video'
    }
    return 'image'
}

export function normalizeFeatureValues(features: FeatureFormValue[]): string[] {
    return features.map((item) => item.value?.trim() ?? '').filter((value) => value.length > 0)
}

export function normalizeSocialLinks(links: SocialLinkFormValue[]): TownSocialLink[] {
    return links
        .map((link) => ({
            type: link.type,
            label: link.label,
            url: link.url?.trim() ?? '',
        }))
        .filter((link) => link.url.length > 0)
}

export function mapMetadataToFormValues(
    metadata: ServerSpaceMetadata | undefined,
    fallback: FormFallbackValues,
): EditTownMetadataFormValues {
    const textFields = metadata?.textFields ?? {}

    const featuresFromMetadata = parseJsonField<string[]>(
        textFields[SPACE_METADATA_TEXT_FIELD_KEYS.features],
        [],
    )
    const features: FeatureFormValue[] = featuresFromMetadata.map((value) => ({ value }))

    while (features.length < MIN_FEATURES) {
        features.push({ value: '' })
    }
    if (features.length > MAX_FEATURES) {
        features.splice(MAX_FEATURES)
    }

    const socialLinksFromMetadata = parseJsonField<TownSocialLink[]>(
        textFields[SPACE_METADATA_TEXT_FIELD_KEYS.socialLinks],
        [],
    )

    // Always start with all default social links, then merge in saved values
    const socialLinks: SocialLinkFormValue[] = getDefaultSocialLinks().map((defaultLink) => {
        const savedLink = socialLinksFromMetadata.find((link) => link.type === defaultLink.type)
        return savedLink
            ? {
                  type: savedLink.type,
                  label: savedLink.label ?? defaultLink.label,
                  url: savedLink.url,
              }
            : defaultLink
    })

    const faqs: FAQFormValue[] = (metadata?.faq ?? [])
        .sort((a, b) => a.order - b.order)
        .map((faq) => ({
            question: faq.question ?? '',
            answer: faq.answer ?? '',
        }))

    if (faqs.length === 0) {
        faqs.push({ question: '', answer: '' })
    }

    const media: MediaFormValue[] = (metadata?.media ?? [])
        .sort((a, b) => a.order - b.order)
        .filter((item) => item.key)

    return {
        name: fallback.fallbackName ?? '',
        motto: textFields[SPACE_METADATA_TEXT_FIELD_KEYS.motto] ?? fallback.fallbackMotto ?? '',
        category: metadata?.category ?? 'Trading',
        aboutTown:
            textFields[SPACE_METADATA_TEXT_FIELD_KEYS.aboutTown] ??
            fallback.fallbackAboutTown ??
            '',
        aboutCreator: textFields[SPACE_METADATA_TEXT_FIELD_KEYS.aboutCreator] ?? '',
        creatorDisplayName: textFields[SPACE_METADATA_TEXT_FIELD_KEYS.creatorDisplayName] ?? '',
        media,
        features,
        faqs,
        socialLinks,
    }
}

export function buildSpaceMetadataPayload(values: EditTownMetadataFormValues): ServerSpaceMetadata {
    const features = normalizeFeatureValues(values.features ?? [])
    const socialLinks = normalizeSocialLinks(values.socialLinks ?? [])
    const faqs = (values.faqs ?? [])
        .map((faq, index) => ({
            question: faq.question?.trim() ?? '',
            answer: faq.answer?.trim() ?? '',
            order: index,
        }))
        .filter((faq) => faq.question.length > 0 || faq.answer.length > 0)

    const textFields: Record<string, string> = {
        [SPACE_METADATA_TEXT_FIELD_KEYS.motto]: values.motto ?? '',
        [SPACE_METADATA_TEXT_FIELD_KEYS.aboutTown]: values.aboutTown ?? '',
        [SPACE_METADATA_TEXT_FIELD_KEYS.aboutCreator]: values.aboutCreator ?? '',
        [SPACE_METADATA_TEXT_FIELD_KEYS.creatorDisplayName]: values.creatorDisplayName ?? '',
        [SPACE_METADATA_TEXT_FIELD_KEYS.features]: JSON.stringify(features),
        [SPACE_METADATA_TEXT_FIELD_KEYS.socialLinks]: JSON.stringify(socialLinks),
    }

    return {
        category: values.category ?? 'Trading',
        textFields,
        media: (values.media ?? [])
            .filter((item): item is MediaFormValue & { key: string } => Boolean(item.key))
            .map(
                (item, index) =>
                    ({
                        key: item.key,
                        type: item.type === 'video' ? 'video' : 'image',
                        order: index,
                        ...(item.type === 'video' ? { muxPlaybackId: item.muxPlaybackId } : {}),
                    }) satisfies Omit<ServerSpaceMetadata['media'][number], 'variants'>,
            ),
        faq: faqs,
    }
}
